import React, { useState, useEffect } from 'react';
import { GROUPS } from '../linksData';

const STORAGE_KEY = 'links-view-mode';

export default function Links({ category = null }) {
  const [mode, setMode] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || 'tiles'; } catch { return 'tiles'; }
  });
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, mode); } catch {}
  }, [mode]);

  // When a category is passed (ribbon master-detail), show only that group and use
  // its name as the heading. With no category (classic Links page) show everything.
  const matched = category ? GROUPS.filter(g => g.label === category) : GROUPS;
  const groups = (category && matched.length === 0) ? GROUPS : matched;

  return (
    <div style={styles.page}>
      <div style={styles.headerRow}>
        <div>
          <h1 style={styles.h1}>{category || 'Links'}</h1>
          <p style={styles.sub}>{category ? 'Opens in a new tab.' : 'Quick access to internal systems, state portals, and vendor tools.'}</p>
        </div>
        <div style={styles.toggle}>
          <button
            style={{...styles.toggleBtn, ...(mode === 'tiles' ? styles.toggleBtnActive : {})}}
            onClick={() => setMode('tiles')}
            title="Tile view"
            aria-label="Tile view"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
              <rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
            </svg>
          </button>
          <button
            style={{...styles.toggleBtn, ...(mode === 'list' ? styles.toggleBtnActive : {})}}
            onClick={() => setMode('list')}
            title="List view"
            aria-label="List view"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/>
              <line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>
            </svg>
          </button>
        </div>
      </div>

      {groups.map(group => (
        <section key={group.label} style={styles.group}>
          {!category && <div style={styles.groupLabel}>{group.label}</div>}
          {mode === 'tiles' ? <TileGrid links={group.links} /> : <LinkList links={group.links} />}
        </section>
      ))}
    </div>
  );
}

function TileGrid({ links }) {
  return (
    <div style={styles.grid}>
      {links.map(link => (
        <a
          key={link.url}
          href={link.url}
          target="_blank"
          rel="noopener noreferrer"
          style={styles.tile}
          onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.08)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)'; }}
        >
          <div style={{...styles.tileHeader, background: `linear-gradient(135deg, ${link.tone} 0%, ${darken(link.tone)} 100%)`}}>
            <div style={styles.tileWordmark}>{link.name}</div>
          </div>
          <div style={styles.tileBody}>
            <div style={styles.tileName}>{link.name}</div>
            <div style={styles.tileDesc}>{link.desc}</div>
            <div style={styles.tileFooter}>
              <span style={styles.tileHost}>{prettyHost(link.url)}</span>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{color: 'var(--caam-text-light)'}}>
                <path d="M7 17L17 7"/><path d="M7 7h10v10"/>
              </svg>
            </div>
          </div>
        </a>
      ))}
    </div>
  );
}

function LinkList({ links }) {
  return (
    <div style={styles.list}>
      {links.map(link => (
        <a
          key={link.url}
          href={link.url}
          target="_blank"
          rel="noopener noreferrer"
          style={styles.row}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--caam-cream-deep)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; }}
        >
          <div style={{...styles.rowSwatch, background: link.tone}} />
          <div style={styles.rowMiddle}>
            <div style={styles.rowTopLine}>
              <span style={styles.rowName}>{link.name}</span>
              <span style={styles.rowHost}>{prettyHost(link.url)}</span>
            </div>
            <div style={styles.rowDesc}>{link.desc}</div>
          </div>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{color: 'var(--caam-text-light)', flexShrink: 0}}>
            <path d="M7 17L17 7"/><path d="M7 7h10v10"/>
          </svg>
        </a>
      ))}
    </div>
  );
}

function prettyHost(url) {
  try { return new URL(url).hostname.replace(/^www\./, ''); }
  catch { return url.replace(/^https?:\/\//, '').split('/')[0]; }
}

function darken(hex) {
  const m = hex.match(/^#([0-9a-f]{6})$/i);
  if (!m) return hex;
  const [r, g, b] = [0, 2, 4].map(i => parseInt(m[1].substr(i, 2), 16));
  const d = (c) => Math.max(0, Math.floor(c * 0.78)).toString(16).padStart(2, '0');
  return `#${d(r)}${d(g)}${d(b)}`;
}

const styles = {
  page: { padding: '32px 40px', maxWidth: 1400, margin: '0 auto' },
  headerRow: { marginBottom: 28, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 24 },
  h1: { margin: 0, fontSize: 26, fontWeight: 600, color: 'var(--caam-text)', letterSpacing: '-0.01em' },
  sub: { margin: '6px 0 0', color: 'var(--caam-text-muted)', fontSize: 13 },
  toggle: { display: 'flex', alignItems: 'center', gap: 2, background: 'var(--caam-cream-deep)', border: '0.5px solid var(--caam-border)', borderRadius: 8, padding: 3, flexShrink: 0 },
  toggleBtn: { background: 'transparent', border: 'none', padding: '6px 10px', borderRadius: 6, cursor: 'pointer', color: 'var(--caam-text-muted)', display: 'flex', alignItems: 'center', transition: 'background-color 0.12s ease, color 0.12s ease' },
  toggleBtnActive: { background: '#fff', color: 'var(--caam-accent-dark)', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },

  group: { marginBottom: 36 },
  groupLabel: { fontSize: 11, color: 'var(--caam-text-muted)', letterSpacing: '0.04em', textTransform: 'uppercase', fontWeight: 600, marginBottom: 12 },

  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 },
  tile: { background: '#fff', border: '0.5px solid var(--caam-border)', borderRadius: 10, overflow: 'hidden', textDecoration: 'none', color: 'inherit', display: 'block', transition: 'transform 0.15s ease, box-shadow 0.15s ease', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
  tileHeader: { height: 88, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 },
  tileWordmark: { color: '#fff', fontSize: 16, fontWeight: 600, letterSpacing: '-0.01em', textShadow: '0 1px 2px rgba(0,0,0,0.15)', textAlign: 'center' },
  tileBody: { padding: '14px 16px 12px' },
  tileName: { fontSize: 14, color: 'var(--caam-text)', fontWeight: 500, marginBottom: 4 },
  tileDesc: { fontSize: 12, color: 'var(--caam-text-muted)', lineHeight: 1.5, marginBottom: 10, minHeight: 32 },
  tileFooter: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  tileHost: { fontSize: 11, color: 'var(--caam-text-light)', fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace' },

  list: { display: 'flex', flexDirection: 'column', background: '#fff', border: '0.5px solid var(--caam-border)', borderRadius: 10, overflow: 'hidden' },
  row: { display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px', textDecoration: 'none', color: 'inherit', borderBottom: '0.5px solid var(--caam-border)', transition: 'background-color 0.12s ease' },
  rowSwatch: { width: 32, height: 32, borderRadius: 6, flexShrink: 0 },
  rowMiddle: { flex: 1, minWidth: 0 },
  rowTopLine: { display: 'flex', alignItems: 'baseline', gap: 10 },
  rowName: { fontSize: 13.5, fontWeight: 500, color: 'var(--caam-text)' },
  rowHost: { fontSize: 11, color: 'var(--caam-text-light)', fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace' },
  rowDesc: { fontSize: 12, color: 'var(--caam-text-muted)', marginTop: 2 }
};
